import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Home as HomeIcon, MapPin, Search, ShieldCheck, Heart, Dog, ArrowLeft, Send, ChevronRight, HeartHandshake, AlertTriangle, X, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* -------------------------- LIST ALL SHELTERS -------------------------- */
export function SheltersList() {
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shelters", { params: city ? { city } : {} });
      setItems(data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-32" data-testid="shelters-list-page">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-1.5">
        <HomeIcon className="h-3 w-3" /> Rifugi & Associazioni
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
        I rifugi <span className="orio-grad-text">della tua zona</span>
      </h1>
      <p className="text-[#9A9A9A] mt-2 text-sm max-w-xl">
        Adotta, fai stallo, fai volontariato. Ogni casa che si apre cambia una vita.
      </p>

      <div className="mt-6 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A]" />
          <input
            data-testid="shelters-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Filtra per città"
            className="w-full pl-9 pr-4 py-2.5 rounded-full bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 text-sm"
          />
        </div>
        <button onClick={load} data-testid="shelters-search"
          className="px-4 py-2.5 rounded-full bg-white text-black text-sm font-medium">
          Cerca
        </button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          [1,2,3,4].map((i) => (<div key={i} className="rounded-2xl bg-white/[0.02] border border-[#F0EBE3] h-32 animate-pulse" />))
        ) : items.length === 0 ? (
          <div className="col-span-full glass rounded-3xl p-10 text-center text-[#6B6B6B] text-sm">
            Nessun rifugio trovato.
          </div>
        ) : items.map((s) => (
          <Link to={`/app/rifugi/${s.id}`} key={s.id} data-testid={`shelter-card-${s.id}`}
            className="rounded-2xl p-4 border transition-all hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(155deg, rgba(255,176,32,0.10) 0%, rgba(10,10,10,0.6) 60%)",
              borderColor: "rgba(255,176,32,0.30)",
            }}>
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-[#FFB020]/15 flex items-center justify-center text-[#FFB020] shrink-0">
                {s.photo_url ? <img src={s.photo_url} alt="" className="h-full w-full object-cover rounded-xl" /> : <HomeIcon className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="font-display text-base leading-tight truncate">{s.name}</div>
                  {s.verified && <ShieldCheck className="h-3.5 w-3.5 text-[#34D399] shrink-0" />}
                </div>
                {s.city && <div className="text-[11px] text-[#9A9A9A] mt-0.5 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.city}</div>}
                <div className="mt-2 text-[11px]">
                  <span className="inline-flex items-center gap-1 text-[#FFB020]">
                    <Dog className="h-3 w-3" /> {s.adoptable_count} {s.adoptable_count === 1 ? "cane in adozione" : "cani in adozione"}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------ DETAIL ------------------------------ */
export function ShelterDetail() {
  const { sid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adoptDog, setAdoptDog] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shelters/${sid}`);
        setData(data);
      } catch { toast.error("Rifugio non trovato"); }
      finally { setLoading(false); }
    })();
  }, [sid]);

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-10 text-[#9A9A9A] text-sm">Caricamento…</div>;
  if (!data) return null;
  const s = data.shelter;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-32" data-testid="shelter-detail-page">
      <Link to="/app/rifugi" className="inline-flex items-center gap-1 text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A] mb-4">
        <ArrowLeft className="h-3 w-3" /> Tutti i rifugi
      </Link>

      <div className="rounded-3xl p-5 sm:p-6 border" style={{
        background: "linear-gradient(155deg, rgba(255,176,32,0.12) 0%, rgba(10,10,10,0.7) 60%)",
        borderColor: "rgba(255,176,32,0.30)",
      }}>
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-2xl bg-[#FFB020]/15 flex items-center justify-center text-[#FFB020] shrink-0">
            {s.photo_url ? <img src={s.photo_url} alt="" className="h-full w-full object-cover rounded-2xl" /> : <HomeIcon className="h-7 w-7" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">{s.name}</h1>
              {s.verified && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(52,211,153,0.15)", color: "#34D399" }}>
                  <ShieldCheck className="h-3 w-3" /> Verificato
                </span>
              )}
            </div>
            {s.city && <div className="text-sm text-[#9A9A9A] mt-1 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.city}</div>}
            {s.bio && <p className="text-sm text-[#6B6B6B] mt-3 leading-relaxed">{s.bio}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {s.phone && (
                <a href={`tel:${s.phone.replace(/\s/g,"")}`} data-testid="shelter-call"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border"
                  style={{ background: "rgba(52,211,153,0.15)", borderColor: "rgba(52,211,153,0.45)", color: "#34D399" }}>
                  <Phone className="h-3 w-3" /> {s.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Adoptable dogs */}
      <section className="mt-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB020] mb-2 flex items-center gap-1.5">
          <Heart className="h-3 w-3" /> Cani in adozione
        </div>
        <h2 className="font-display text-2xl tracking-tight">Trova il tuo compagno</h2>

        {data.adoptable_dogs.length === 0 ? (
          <div className="mt-4 rounded-2xl bg-white/[0.025] border border-[#F0EBE3] p-8 text-center text-[#9A9A9A] text-sm">
            Al momento questo rifugio non ha cani in adozione su ORIO.
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.adoptable_dogs.map((d) => (
              <div key={d.id} data-testid={`adoptable-${d.id}`}
                className="rounded-2xl orio-card overflow-hidden">
                <div className="aspect-[4/3] bg-[#FAF7F0] relative overflow-hidden">
                  {d.photo_url ? (
                    <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Dog className="h-12 w-12 text-[#1A1A1A]/20" /></div>
                  )}
                  {d.adoption_status === "reserved" && (
                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(255,176,32,0.20)", color: "#FFB020" }}>Riservato</span>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-display text-lg leading-tight">{d.name}</div>
                  <div className="text-[11px] text-[#9A9A9A] mt-0.5">
                    {[d.breed, d.sex].filter(Boolean).join(" · ") || "Meticcio"}
                  </div>
                  {d.adoption_story && <p className="text-[12px] text-[#6B6B6B] mt-2 line-clamp-3">{d.adoption_story}</p>}
                  <button
                    data-testid={`adopt-cta-${d.id}`}
                    disabled={!user || user.id === sid || d.adoption_status === "adopted"}
                    onClick={() => setAdoptDog(d)}
                    className="mt-3 w-full px-3 py-2 rounded-full text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    style={{ background: "#FFB020", color: "#000" }}
                  >
                    <Heart className="h-3.5 w-3.5" /> Voglio adottare
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Open missions of the shelter */}
      {data.missions.length > 0 && (
        <section className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF3B30] mb-2 flex items-center gap-1.5">
            <HeartHandshake className="h-3 w-3" /> Missioni aperte
          </div>
          <h2 className="font-display text-2xl tracking-tight">Come aiutare ora</h2>
          <div className="mt-4 space-y-2">
            {data.missions.map((m) => (
              <Link key={m.id} to={`/app/missioni/${m.id}`}
                className="block rounded-2xl border border-[#F0EBE3] bg-white/[0.025] hover:bg-[#FAF7F0] p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-[#FFB020]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-[#9A9A9A]">{m.code}</div>
                    <div className="text-sm font-medium truncate">{m.title}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#9A9A9A]" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {adoptDog && (
        <AdoptDialog dog={adoptDog} onClose={() => setAdoptDog(null)} onDone={() => setAdoptDog(null)} />
      )}
    </div>
  );
}

function AdoptDialog({ dog, onClose, onDone }) {
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (msg.trim().length < 20) return toast.error("Scrivi almeno 20 caratteri");
    setLoading(true);
    try {
      await api.post(`/dogs/${dog.id}/adopt-candidacy`, { message: msg });
      toast.success("Candidatura inviata. Il rifugio ti contatterà.");
      onDone();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[1500] bg-[#FBF7F1]/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <form data-testid="adopt-dialog" onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="orio-card rounded-3xl w-full max-w-md p-5 sm:p-6 space-y-4 relative">
        <button type="button" onClick={onClose} aria-label="Chiudi"
          className="absolute right-3 top-3 h-8 w-8 rounded-full bg-[#FAF7F0] hover:bg-[#F4EFE6] flex items-center justify-center">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#FFB020]/15 flex items-center justify-center text-[#FFB020] shrink-0">
            {dog.photo_url ? <img src={dog.photo_url} alt="" className="w-full h-full object-cover rounded-xl" /> : <Dog className="h-5 w-5" />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">Voglio adottare</div>
            <h3 className="font-display text-xl leading-tight">{dog.name}</h3>
          </div>
        </div>
        <textarea
          data-testid="adopt-message"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="Raccontami di te, dei tuoi spazi, della tua esperienza con i cani. Il rifugio leggerà questo messaggio."
          minLength={20}
          maxLength={1000}
          required
          className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none focus:border-white/30 min-h-[160px] text-sm"
        />
        <div className="text-[11px] text-[#9A9A9A]">{msg.length}/1000</div>
        <button type="submit" disabled={loading} data-testid="adopt-submit"
          className="orio-cta-btn relative w-full px-5 py-3 rounded-full font-semibold text-black overflow-hidden disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <span className="relative z-10 inline-flex items-center gap-2">
            <Send className="h-4 w-4" /> {loading ? "Invio…" : "Invia candidatura"}
          </span>
        </button>
      </form>
    </div>
  );
}

/* ----------------- SHELTER CANDIDACIES INBOX (shelter side) ----------------- */
export function ShelterCandidacies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shelter/candidacies");
      setItems(data);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (cid, status) => {
    try {
      await api.post(`/shelter/candidacies/${cid}/status`, { status });
      toast.success("Aggiornato");
      load();
    } catch { toast.error("Errore"); }
  };

  if (user && !user.is_shelter) {
    return <div className="max-w-3xl mx-auto px-6 py-10 text-[#9A9A9A] text-sm">Solo gli account rifugio possono accedere.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-32" data-testid="shelter-candidacies-page">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-1.5">Inbox rifugio</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Candidature ricevute</h1>
      <p className="text-[#9A9A9A] mt-2 text-sm">Persone che vogliono adottare i tuoi cani.</p>

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="text-[#9A9A9A] text-sm py-8 text-center">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="glass rounded-3xl p-10 text-center text-[#6B6B6B] text-sm">Nessuna candidatura ricevuta.</div>
        ) : (
          items.map((c) => {
            const color = c.status === "accepted" ? "#34D399" : c.status === "rejected" ? "#FF3B30" : "#FFB020";
            return (
              <div key={c.id} className="rounded-2xl border p-4" style={{
                background: `linear-gradient(155deg, ${color}10 0%, rgba(10,10,10,0.6) 60%)`,
                borderColor: `${color}33`,
              }}>
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-xl overflow-hidden bg-[#FAF7F0] shrink-0">
                    {c.dog_photo ? <img src={c.dog_photo} alt="" className="w-full h-full object-cover" /> : <Dog className="w-full h-full p-3 text-[#C7C0B3]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${color}22`, color }}>
                        {c.status}
                      </span>
                      <span className="text-[11px] text-[#9A9A9A]">{new Date(c.created_at).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="font-medium mt-1">Per <span className="text-[#FFB020]">{c.dog_name}</span></div>
                    <div className="text-sm text-[#6B6B6B] mt-1">
                      <strong>{c.candidate_name}</strong>{c.candidate_city ? ` · ${c.candidate_city}` : ""}
                    </div>
                    <p className="text-sm text-[#1A1A1A] mt-2 whitespace-pre-line">{c.message}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                      {c.candidate_phone && <a href={`tel:${c.candidate_phone.replace(/\s/g,"")}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FAF7F0] border border-[#F0EBE3]"><Phone className="h-3 w-3" />{c.candidate_phone}</a>}
                      {c.candidate_email && <a href={`mailto:${c.candidate_email}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FAF7F0] border border-[#F0EBE3]"><Mail className="h-3 w-3" />{c.candidate_email}</a>}
                    </div>
                  </div>
                </div>
                {c.status === "pending" && (
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => setStatus(c.id, "accepted")} data-testid={`cand-accept-${c.id}`}
                      className="px-4 py-2 rounded-full text-sm font-semibold" style={{ background: "#34D399", color: "#000" }}>
                      Accetta
                    </button>
                    <button onClick={() => setStatus(c.id, "rejected")} data-testid={`cand-reject-${c.id}`}
                      className="px-4 py-2 rounded-full text-sm bg-[#FAF7F0] border border-[#F0EBE3]">
                      Rifiuta
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
